import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Gallery({ images, testid = "gallery" }) {
  const [idx, setIdx] = useState(-1);
  const open = idx >= 0;
  const close = useCallback(() => setIdx(-1), []);
  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, prev, next]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid={testid}>
        {images.map((src, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`${testid}-item-${i}`}
            className="group relative overflow-hidden rounded-2xl aspect-[4/3] shadow-sm cursor-zoom-in"
          >
            <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            <span className="absolute inset-0 bg-[#0B2A6B]/0 group-hover:bg-[#0B2A6B]/20 transition" />
          </button>
        ))}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[96] flex items-center justify-center bg-black/90 p-4 animate-fade-up"
          data-testid="lightbox"
          onClick={close}
        >
          <button onClick={close} data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white transition">
            <X className="h-8 w-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            className="absolute left-3 md:left-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
          <img
            src={images[idx]}
            alt=""
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            className="absolute right-3 md:right-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
            {idx + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
}
